import { cloneDeep } from "lodash";
import React, { FC, useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
  ResponderProvided,
} from "react-beautiful-dnd";
import { useSelector } from "react-redux";

import { IgButton, IgPaper } from "@intelligems/ig-theme";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import { Divider, Grid, Popover, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { getVariationDtoFromFe } from "../../api/formatters/from-dto/experiences/variations";
import { usePatchVariations } from "../../api/variations/patch-variation";
import { selectedVariations } from "../../store/slices/variations";
import { ExperienceFe } from "../../types/schemas/experiences";
import { VariationFe } from "../../types/schemas/variations";
import { sortByOrder } from "../../utils/sortings";

const PREFIX = "TestGroupOrderPopover";

const classes = {
  dragIcon: `${PREFIX}-dragIcon`,
  paper: `${PREFIX}-paper`,
  row: `${PREFIX}-row`,
  dragBurgerIcon: `${PREFIX}-dragBurgerIcon`,
};

const Root = styled(Grid)(({ theme }) => ({
  [`& .${classes.dragIcon}`]: {
    color: "rgba(0,0,0,0.38)",
    transform: "rotate(90deg)",
    "&:hover, &:focus": {
      color: "#0013BC",
      transform: "rotate(90deg) scale(1.25)",
    },
  },

  [`& .${classes.paper}`]: {
    minHeight: "25px",
    minWidth: "200px",
  },

  [`& .${classes.row}`]: {
    backgroundColor: "white",
  },

  [`& .${classes.dragBurgerIcon}`]: {
    marginTop: "4px",
    "&:hover, &:focus": {
      color: "#0013BC",
      transform: "scale(1.25)",
    },
  },
}));

interface VariationOrderPopoverProps {
  experience: ExperienceFe;
  id: string;
}

export const VariationOrderPopover = ({
  id,
  experience,
}: VariationOrderPopoverProps) => {
  const patchTestVariations = usePatchVariations(
    experience.category,
    experience.id
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [variations, setVariations] = useState<VariationFe[]>([]);
  const selectedVariationsState = useSelector(selectedVariations);
  useEffect(() => {
    if (experience) {
      setVariations(cloneDeep(selectedVariationsState));
    }
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSaveClick = () => {
    if (experience) {
      patchTestVariations.mutate({
        variations: getVariationDtoFromFe(variations),
      });
    }
  };

  const handleReorder = (
    testGroups: VariationFe[],
    startIndex: number,
    endIndex: number
  ) => {
    const result = cloneDeep(testGroups);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result.map((tg, index) => ({
      ...tg,
      order: index,
    }));
  };

  const onDragEnd = (result: DropResult, provided: ResponderProvided) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    if (variations) {
      const newTestGroups = handleReorder(
        variations,
        result.source.index,
        result.destination.index
      );
      setVariations(newTestGroups);
    }
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? `test-group-order-popover-${id}` : undefined;

  if (variations) {
    return (
      <Root>
        <span onClick={handleClick}>
          <ImportExportIcon className={classes.dragIcon} />
        </span>
        <Popover
          id={popoverId}
          open={open}
          anchorEl={anchorEl}
          elevation={0}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <IgPaper className={classes.paper}>
            <Grid container direction={"column"} rowSpacing={1}>
              <Grid item xs>
                <Grid container direction={"row"} justifyContent={"center"}>
                  <Grid item xs style={{ flexGrow: 0 }}>
                    <Typography variant="body1" noWrap={true}>
                      Order Test Group Bars
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable
                    droppableId={"test-group-zone"}
                    direction={"vertical"}
                  >
                    {(
                      provided: DroppableProvided,
                      snapshot: DroppableStateSnapshot
                    ) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {variations.sort(sortByOrder).map((tg, index) => (
                          <Draggable
                            key={`${popoverId}-${index}`}
                            draggableId={`${popoverId}-${index}`}
                            index={index}
                          >
                            {(
                              provided: DraggableProvided,
                              snapshot: DraggableStateSnapshot
                            ) => {
                              // Restrict dragging to vertical axis

                              let transform =
                                // @ts-ignore
                                provided.draggableProps.style.transform;

                              if (snapshot.isDragging && transform) {
                                transform = transform.replace(/\(.+,/, "(0,");
                              }

                              const style = {
                                ...provided.draggableProps.style,
                                transform,
                              };
                              return (
                                <Grid
                                  container
                                  key={`test-group-popover-${id}-item-${index}`}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  style={style}
                                >
                                  <Grid item xs className={classes.row}>
                                    <Grid
                                      container
                                      direction={"row"}
                                      justifyContent={"space-between"}
                                      alignItems={"center"}
                                    >
                                      <Grid
                                        item
                                        xs
                                        style={{ maxHeight: "28px" }}
                                      >
                                        <Typography
                                          variant="body2"
                                          noWrap={true}
                                        >
                                          {tg.name}
                                        </Typography>
                                      </Grid>
                                      <Grid
                                        item
                                        xs
                                        style={{ maxWidth: "28px" }}
                                      >
                                        <span {...provided.dragHandleProps}>
                                          <DragHandleIcon
                                            className={classes.dragBurgerIcon}
                                          />
                                        </span>
                                      </Grid>
                                    </Grid>
                                    <Divider />
                                  </Grid>
                                </Grid>
                              );
                            }}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </Grid>
              <Grid item xs>
                <Grid container direction={"row"} justifyContent={"center"}>
                  <Grid item xs style={{ flexGrow: 0 }}>
                    <IgButton
                      size={"small"}
                      loading={patchTestVariations.isPending}
                      variant="contained"
                      color="primary"
                      onClick={onSaveClick}
                    >
                      Save
                    </IgButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </IgPaper>
        </Popover>
      </Root>
    );
  }
  return null;
};
